import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export type SupportTicketStatus = 'bot' | 'pending_admin' | 'open' | 'resolved' | 'closed';
export type SupportTicketCategory = 'offer' | 'mission' | 'account' | 'payment' | 'other';
export type SupportSenderType = 'user' | 'bot' | 'admin';

export interface SupportTicket {
  id: string;
  user_id: string;
  category: SupportTicketCategory | null;
  related_order_id: string | null;
  related_bid_id: string | null;
  subject: string | null;
  status: SupportTicketStatus;
  assigned_admin_id: string | null;
  last_message_at: string;
  unread_for_user: boolean;
  unread_for_admin: boolean;
  created_at: string;
  updated_at: string;
}

export interface SupportMessage {
  id: string;
  ticket_id: string;
  sender_id: string | null;
  sender_type: SupportSenderType;
  content: string;
  metadata: any;
  read_at: string | null;
  created_at: string;
}

/** Tickets visible à l'utilisateur courant (ses propres tickets, ou tous si admin). */
export const useSupportTickets = (opts?: { adminView?: boolean }) => {
  const { user } = useAuth();
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ['support-tickets', user?.id, opts?.adminView],
    queryFn: async () => {
      let q = supabase
        .from('support_tickets')
        .select('*')
        .order('last_message_at', { ascending: false });
      if (!opts?.adminView && user) q = q.eq('user_id', user.id);
      const { data, error } = await q;
      if (error) throw error;
      return (data || []) as SupportTicket[];
    },
    enabled: !!user,
  });

  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel(`support-tickets-${user.id}-${opts?.adminView ? 'admin' : 'user'}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'support_tickets' },
        () => {
          qc.invalidateQueries({ queryKey: ['support-tickets'] });
        }
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, opts?.adminView, qc]);

  return query;
};

/** Messages d'un ticket précis, avec realtime. */
export const useSupportMessages = (ticketId: string | null) => {
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ['support-messages', ticketId],
    queryFn: async () => {
      if (!ticketId) return [];
      const { data, error } = await supabase
        .from('support_messages')
        .select('*')
        .eq('ticket_id', ticketId)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return (data || []) as SupportMessage[];
    },
    enabled: !!ticketId,
  });

  useEffect(() => {
    if (!ticketId) return;
    const channel = supabase
      .channel(`support-messages-${ticketId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'support_messages', filter: `ticket_id=eq.${ticketId}` },
        () => {
          qc.invalidateQueries({ queryKey: ['support-messages', ticketId] });
        }
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [ticketId, qc]);

  return query;
};

export const useCreateTicket = () => {
  const { user } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (subject?: string) => {
      if (!user) throw new Error('Not authenticated');
      const { data, error } = await supabase
        .from('support_tickets')
        .insert({ user_id: user.id, status: 'bot', subject: subject || null })
        .select()
        .single();
      if (error) throw error;
      return data as SupportTicket;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['support-tickets'] }),
  });
};

export const useSendMessage = () => {
  const { user } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      ticketId: string;
      content: string;
      senderType: SupportSenderType;
      metadata?: any;
    }) => {
      const { error } = await supabase.from('support_messages').insert({
        ticket_id: args.ticketId,
        sender_id: args.senderType === 'bot' ? null : user?.id ?? null,
        sender_type: args.senderType,
        content: args.content,
        metadata: args.metadata ?? null,
      });
      if (error) throw error;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['support-messages', vars.ticketId] });
      qc.invalidateQueries({ queryKey: ['support-tickets'] });
    },
  });
};

export const useUpdateTicket = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string; patch: Partial<SupportTicket> }) => {
      const { error } = await supabase
        .from('support_tickets')
        .update(args.patch)
        .eq('id', args.id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['support-tickets'] }),
  });
};

/** Marque les messages comme lus pour le côté courant. */
export const useMarkTicketRead = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { ticketId: string; side: 'user' | 'admin' }) => {
      const patch =
        args.side === 'user' ? { unread_for_user: false } : { unread_for_admin: false };
      const { error } = await supabase
        .from('support_tickets')
        .update(patch)
        .eq('id', args.ticketId);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['support-tickets'] }),
  });
};
